import { useTheme, createTheme, Theme } from '@mui/material';
import { blueGrey } from '@mui/material/colors';

export const BORDER_CONFIG = '0.5px solid #000000';

export const menuStyle = (theme: Theme) => ({
  transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) !important',
  transformOrigin: 'top center !important',
  '& .MuiPaper-root': {
    animation: 'menuSlideDown 400ms cubic-bezier(0.4, 0, 0.2, 1) !important',
    backgroundColor: 'white',
    borderRadius: '13px',
    border: BORDER_CONFIG,
    boxShadow: 'none',
    '&::-webkit-scrollbar': {
      width: '4px'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '10px',
      backgroundColor: theme.palette.grey[200],
      marginTop: '12px',
      marginBottom: '12px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: '#000000'
    },
    '.MuiMenu-list': {
      boxShadow: 'none',
      '& .MuiMenuItem-root': {
        display: 'flex',
        gap: '4px'
      }
    }
  }
});

export const styleOverridesAutocomplete = {
  endAdornment: {
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    position: 'absolute'
  },
  root: {
    '& label.MuiFormLabel-root.MuiInputLabel-root:not(.MuiFormLabel-filled):not(.Mui-focused):not(.MuiInputLabel-shrink)':
      {
        marginLeft: '8px'
      }
  },
  input: {
    marginLeft: '8px'
  }
};

export const styleOverridesSelect = {
  select: {
    paddingRight: '30px',
    paddingLeft: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    height: '37px',
    boxSizing: 'border-box' as const,
    '&.MuiInputBase-inputSizeSmall': {
      height: '31px'
    }
  },
  icon: {
    right: '10px',
    width: '1.25rem'
  }
};

const FormProviderTheme = () => {
  const theme = useTheme();
  const formTheme = createTheme({
    ...theme,
    components: {
      ...theme.components,
      MuiInputBase: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            ...(ownerState?.multiline && {
              overflow: 'auto'
            }),
            backgroundColor: blueGrey[50],
            border: 'none',
            borderRadius: '50px',
            lineHeight: '166%',
            '&:hover': {
              backgroundColor: `${blueGrey[100]} !important`
            },
            '&.Mui-disabled': {
              backgroundColor: `${theme.palette.grey[200]} !important`
            },
            '&.MuiInputBase-sizeSmall': {
              fontSize: '0.875rem',
              '& input': {
                [theme.breakpoints.down('md')]: {
                  height: '40px',
                  boxSizing: 'border-box'
                },
                paddingTop: '5.5px',
                paddingBottom: '5.5px'
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1rem',
                color: theme.palette.grey[500]
              },
              '& .MuiInputAdornment-root': {
                fontSize: '1rem',
                color: theme.palette.grey[500]
              }
            }
          })
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              border: 'none'
            },
            '&.Mui-focused': {
              backgroundColor: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: 'none',
              '& fieldset': {
                border: 'none'
              },
              '&:hover': {
                backgroundColor: 'white !important',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: 'none',
                '& fieldset': {
                  border: 'none'
                }
              }
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: {
            color: '#000000',
            top: '-10px',
            fontWeight: 700,
            transition: 'none',
            '&.Mui-disabled': {
              color: '#00000038'
            }
          },
          root: {
            '&:not(.MuiFormLabel-filled):not(.Mui-focused)': {
              color: '#000000',
              top: '-26px',
              fontWeight: 700,
              transition: 'none',
              '&.Mui-disabled': {
                color: '#00000038'
              },
              '&.MuiInputLabel-sizeSmall': {
                fontSize: '0.75rem'
              }
            }
          }
        }
      },
      MuiAutocomplete: {
        ...theme.components?.MuiAutocomplete,
        styleOverrides: {
          inputRoot: {
            height: '37px',
            boxSizing: 'border-box',
            paddingRight: '9px !important',
            position: 'relative',
            '&.MuiInputBase-sizeSmall': {
              height: '31px',
              [theme.breakpoints.down('md')]: {
                height: '40px'
              },
              boxSizing: 'border-box'
            }
          },
          endAdornment: () => ({
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            position: 'absolute' as const
          }),
          input: styleOverridesAutocomplete.input,
          popper: ({ theme }) => menuStyle(theme),
          hasClearIcon: {
            '& input.MuiAutocomplete-input': {
              marginLeft: '43px'
            }
          }
        }
      },
      MuiSelect: {
        ...theme.components?.MuiSelect,
        styleOverrides: {
          select: ({ theme }) => ({
            ...styleOverridesSelect.select,
            '&.MuiInputBase-inputSizeSmall': {
              height: '31px',
              [theme.breakpoints.down('md')]: {
                height: '44px'
              }
            }
          }),
          icon: styleOverridesSelect.icon
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: '#f2f4f9',
            minHeight: '37px'
          }
        }
      },
      MuiGrid: {
        styleOverrides: {
          container: ({ ownerState }) => {
            return {
              alignItems: 'end',
              [theme.breakpoints.down('md')]: {
                '&>.MuiGrid-item': {
                  paddingTop: !isNaN(Number(ownerState.rowSpacing))
                    ? `${Number(ownerState.rowSpacing) * 8 + 10}px`
                    : '0px'
                }
              }
            };
          }
        }
      }
    }
  });
  return formTheme;
};

export default FormProviderTheme;
